import React, { useState, useEffect } from 'react';
import AirportSearch from './AirportSearch';
import { getAirportObjectForCity } from '../../utils/cityToAirport';
import './FlightSearchForm.css';

/**
 * FlightSearchForm - Simple horizontal layout like SanoGrid
 * 
 * Features:
 * - Airport autocomplete (keeps existing functionality)
 * - Horizontal form layout (cleaner)
 * - Round-trip toggle
 * - Auto-populate from trial context
 */
const FlightSearchForm = ({ onSearch, loading = false, trialContext = null }) => {
  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);
  const [departureDate, setDepartureDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [isRoundTrip, setIsRoundTrip] = useState(true);

  const today = new Date().toISOString().split('T')[0];

  // Auto-populate destination from trial context
  useEffect(() => {
    console.log('🔍 FlightSearchForm useEffect - trialContext:', trialContext);
    console.log('🔍 FlightSearchForm useEffect - current destination:', destination);

    if (trialContext && trialContext.destination && !destination) {
      console.log('🎯 Attempting to map city:', trialContext.destination);

      // Map city to airport code
      const airportObj = getAirportObjectForCity(trialContext.destination);

      console.log('✈️ Airport mapping result:', airportObj);

      if (airportObj) {
        setDestination(airportObj);
        console.log('✅ Auto-populated destination:', airportObj);
      } else {
        console.log('⚠️ No airport mapping found for:', trialContext.destination);
      }
      // Pre-populate dates
      if (trialContext?.tripStartDate && !departureDate) {
        setDepartureDate(trialContext.tripStartDate);
      }
      if (trialContext?.tripEndDate && !returnDate) {
        setReturnDate(trialContext.tripEndDate);
      }
    } else {
      console.log('❌ Skipping auto-fill. Reasons:', {
        hasTrialContext: !!trialContext,
        hasDestinationInContext: !!trialContext?.destination,
        destinationAlreadySet: !!destination
      });
    }
  }, [trialContext]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!origin) {
      alert('Please select origin airport');
      return;
    }

    if (!destination) {
      alert('Please select destination airport');
      return;
    }

    if (!departureDate) {
      alert('Please select departure date');
      return;
    }

    if (isRoundTrip && !returnDate) {
      alert('Please select return date for round-trip');
      return;
    }

    if (onSearch) {
      onSearch({
        origin: origin.iataCode,
        destination: destination.iataCode,
        departureDate,
        returnDate: isRoundTrip ? returnDate : null,
        adults: 1,
        travelClass: 'ECONOMY',
        maxResults: 50
      });
    }
  };

  return (
    <form className="simple-flight-form" onSubmit={handleSubmit}>
      {/* Trip Type Toggle */}
      <div className="trip-type-toggle">
        <label className="toggle-option">
          <input
            type="radio"
            checked={isRoundTrip}
            onChange={() => setIsRoundTrip(true)}
          />
          <span>Round-trip</span>
        </label>
        <label className="toggle-option">
          <input
            type="radio"
            checked={!isRoundTrip}
            onChange={() => setIsRoundTrip(false)}
          />
          <span>One-way</span>
        </label>
      </div>

      {/* Form Fields Row */}
      <div className="form-fields-row">
        {/* From */}
        <div className="form-field-simple">
          <label>FROM</label>
          <AirportSearch
            placeholder="Select origin"
            value={origin}
            onChange={setOrigin}
          />
        </div>

        {/* To */}
        <div className="form-field-simple">
          <label>
            TO
            {trialContext && destination && (
              <span className="auto-filled-badge">✓ From trial</span>
            )}
          </label>
          <AirportSearch
            placeholder="Select destination"
            value={destination}
            onChange={setDestination}
          />
        </div>

        {/* Departure Date */}
        <div className="form-field-simple">
          <label>DEPARTURE</label>
          <input
            type="date"
            className="simple-date-input"
            value={departureDate}
            onChange={(e) => setDepartureDate(e.target.value)}
            min={today}
            required
          />
        </div>

        {/* Return Date */}
        {isRoundTrip && (
          <div className="form-field-simple">
            <label>RETURN (OPTIONAL)</label>
            <input
              type="date"
              className="simple-date-input"
              value={returnDate}
              onChange={(e) => setReturnDate(e.target.value)}
              min={departureDate || today}
            />
          </div>
        )}

        {/* Search Button */}
        <div className="form-field-simple search-button-field">
          <button
            type="submit"
            className="simple-search-button"
            disabled={loading}
          >
            {loading ? 'Searching...' : '✈ Search Flights'}
          </button>
        </div>
      </div>
    </form>
  );
};

export default FlightSearchForm;